import React, { useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { X, RefreshCw, MessageSquare, Mic, MicOff, PhoneOff } from 'lucide-react';
import { cn } from '../lib/utils';
import { useLiveCall } from '../context/LiveCallContext';
import { readCut } from '../features/supervision/lib/speech-cut';

function formatTranscriptTime(value) {
  const date = new Date(value || '');
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

export default function LiveCallOverlay() {
  const {
    liveTranscriptTarget,
    liveTranscript,
    liveTranscriptLoading,
    isMinimized,
    closeLiveTranscript,
    minimizeLiveTranscript,
    expandLiveTranscript,
  } = useLiveCall();

  const messagesEndRef = useRef(null);

  // Auto-scroll logic inside the modal
  useEffect(() => {
    if (!isMinimized && messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [liveTranscript?.messages, isMinimized]);

  if (!liveTranscriptTarget || liveTranscriptTarget.inline === true) return null;

  const transcriptData = liveTranscript || liveTranscriptTarget || {};
  const messages = Array.isArray(liveTranscript?.messages) ? liveTranscript.messages : [];
  const title =
    transcriptData.contact_name ||
    transcriptData.customer_name ||
    transcriptData.customer_phone ||
    transcriptData.call_id ||
    'Live Transcript';
  const canToggleMute = typeof liveTranscriptTarget?.onToggleMute === 'function';
  const canHangup = typeof liveTranscriptTarget?.onHangup === 'function';
  const isMicMuted = Boolean(liveTranscriptTarget?.isMicMuted);
  const callStatus = String(liveTranscriptTarget?.callStatus || '').trim();

  if (isMinimized) {
    return (
      <div className="fixed bottom-6 right-6 z-[120]">
        <button
          onClick={expandLiveTranscript}
          className="flex items-center gap-2 px-5 py-3 shadow-2xl rounded-full bg-violet-600 hover:bg-violet-700 text-white font-medium transition-all hover:scale-105 border-2 border-violet-400/50"
        >
          <div className="relative">
            <MessageSquare className="w-5 h-5" />
            <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
          </div>
          <span className="hidden sm:inline">Live Transcript</span>
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      {/* Clickable backdrop that collapses the modal instead of closing it */}
      <div className="absolute inset-0" onClick={minimizeLiveTranscript}></div>
      
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[90vh] border border-slate-200">
        <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100 bg-slate-50/70">
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg font-semibold text-slate-800">{title}</h3>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-100">
                Live
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-500 space-y-0.5">
              {transcriptData.customer_phone && <p>{transcriptData.customer_phone}</p>}
              {transcriptData.call_id && <p>Call ID: {transcriptData.call_id}</p>}
            </div>
          </div>
          <button onClick={closeLiveTranscript} className="text-slate-400 hover:text-slate-600 z-10 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto bg-slate-100/70 px-5 py-5 flex flex-col gap-3">
          {liveTranscriptLoading && !liveTranscript ? (
            <div className="flex items-center justify-center py-16 text-slate-500">
              <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
              Loading live transcript...
            </div>
          ) : messages.length === 0 ? (
            <div className="flex items-center justify-center py-16 text-center text-slate-500">
              <div>
                <p className="font-medium text-slate-600">Call is live.</p>
                <p className="text-sm mt-1">Waiting for the first transcript line to arrive.</p>
              </div>
            </div>
          ) : (
            <>
              {messages.map((message) => {
                const isAssistant = message.role === 'assistant';
                const cut = readCut(message);
                // On a cut turn `text` is what the agent generated, not what came
                // out of the speaker, so render only the spoken part -- including
                // when that is empty. The never-voiced tail is shown struck
                // through underneath.
                const body = cut.visible ? cut.spoken : message.text;
                return (
                  <div
                    key={`${message.item_id || message.index || message.created_at || message.text}`}
                    className={cn(
                      'max-w-[88%] rounded-2xl border px-4 py-3 shadow-sm',
                      isAssistant
                        ? 'mr-auto bg-violet-50 border-violet-100 text-slate-800'
                        : 'ml-auto bg-white border-slate-200 text-slate-700 text-right'
                    )}
                  >
                    <div className="flex items-center justify-between gap-3 mb-1.5">
                      <span className={cn(
                        'text-[10px] font-bold uppercase tracking-wider',
                        isAssistant ? 'text-violet-700' : 'text-slate-500'
                      )}>
                        {isAssistant ? 'Agent' : 'User'}
                      </span>
                      <span className="text-[10px] text-slate-400">
                        {formatTranscriptTime(message.created_at)}
                      </span>
                    </div>
                    <p className="text-sm leading-relaxed whitespace-pre-wrap break-words">{body}</p>
                    {cut.unspoken ? (
                      <p className="mt-1.5 text-[11px] leading-relaxed break-words text-slate-400">
                        <span className="mr-1 text-[9px] font-semibold uppercase tracking-wider">never said</span>
                        <span className="line-through">{cut.unspoken}</span>
                      </p>
                    ) : null}
                    {cut.visible ? (
                      <div className="mt-2">
                        <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-amber-700">
                          {cut.silent ? 'Nothing spoken' : `${Math.round(cut.ratio * 100)}% spoken`}
                        </span>
                        <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-amber-100" title={cut.basis}>
                          <div
                            className={cn('h-full rounded-full', cut.tone === 'total' ? 'bg-red-500' : 'bg-amber-500')}
                            style={{ width: `${Math.max(2, cut.ratio * 100)}%` }}
                          />
                        </div>
                      </div>
                    ) : message.interrupted ? (
                      // A worker from before this feature: the turn was cut but
                      // nothing is known about how far it got.
                      <p className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-amber-600">
                        Interrupted
                      </p>
                    ) : null}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </>
          )}
        </div>

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-100 bg-white">
          <div className="space-y-1">
            <p className="text-xs text-slate-500">
              Streaming live while the call is running.
            </p>
            {callStatus && (
              <p className="text-xs font-medium text-slate-600">{callStatus}</p>
            )}
          </div>
          <div className="flex items-center gap-2">
            {canToggleMute && (
              <Button variant="outline" onClick={liveTranscriptTarget.onToggleMute}>
                {isMicMuted ? <Mic className="w-4 h-4 mr-2" /> : <MicOff className="w-4 h-4 mr-2" />}
                {isMicMuted ? 'Unmute' : 'Mute'}
              </Button>
            )}
            {canHangup && (
              <Button variant="destructive" onClick={liveTranscriptTarget.onHangup}>
                <PhoneOff className="w-4 h-4 mr-2" />
                Hang Up
              </Button>
            )}
            <Button variant="outline" onClick={minimizeLiveTranscript}>Minimize</Button>
            <Button variant="ghost" onClick={closeLiveTranscript}>End Viewing</Button>
          </div>
        </div>
      </div>
    </div>
  );
}
